import axios from "axios";
import { get } from "lodash";
import { CircleAlert } from "lucide-react";
import { createElement } from "react";
import { toast } from "sonner";
import * as tus from "tus-js-client";

import {
  createImage,
  createTrack,
  createVideo,
  getImageUploadUrl,
  getTrackUploadUrl,
  getVideo,
  getVideoUploadUrl,
  updateSKUUniqueContent,
} from "@/data/api";
import { Video } from "@/data/types";

import { AnalyticsEvent, trackEvent } from "./analytics";
import { LGT_SERVER_URL } from "./env";
import { UploadProgressEvent, wrapAxiosProgress } from "./utils";

export enum FileType {
  IMAGE = "image",
  VIDEO = "video",
  AUDIO = "audio",
  CSV = "csv",
}

export type MediaFileType = FileType.IMAGE | FileType.VIDEO | FileType.AUDIO;

export const FileUploadAcceptTypes = {
  [FileType.IMAGE]: "image/png,image/jpeg,image/webp,image/gif",
  [FileType.VIDEO]: "video/quicktime,video/webm",
  [FileType.AUDIO]: "audio/mpeg,audio/wav",
  [FileType.CSV]: "text/csv,application/csv,application/vnd.ms-excel,.csv",
};

export const MaxFileSizeBytes = {
  CloudflareImage: 10485760, // 10MB
  CloudflareVideo: 209715200, // 200MB
  CloudflareTusVideo: 32212254720, // 30GB
  Audio: 31457280, // Omitted the Cloudflare prefix because this is imposed by us
  CSV: 10485760, // 10MB
};

// Cloudflare Stream TUS: min 5MiB, max 200MiB, must be a multiple of 256KiB
// https://developers.cloudflare.com/stream/uploading-videos/resumable-uploads/
const CLOUDFLARE_TUS_CHUNK_SIZE = 52_428_800; // 50MiB


const MaxFileSizeByFileType = {
  [FileType.IMAGE]: MaxFileSizeBytes.CloudflareImage,
  [FileType.VIDEO]: MaxFileSizeBytes.CloudflareTusVideo,
  [FileType.AUDIO]: MaxFileSizeBytes.Audio,
  [FileType.CSV]: MaxFileSizeBytes.CSV,
} as const;

export type UploadedFile = {
  fileType: FileType;
  fileName: string;
  url: string;
};

const MAX_IMAGE_HEIGHT = 10000;
const MAX_IMAGE_WIDTH = 10000;

export const HEADER_LOGO_MAX_SIZE = 5 * 1024 * 1024;
export const HEADER_LOGO_MIME_TYPES = "image/png,.png,image/svg+xml,.svg";

export const uploadImageToCloudflare = async (
  file: File,
  onUploadProgress: UploadProgressEvent,
) => {
  const formData = new FormData();
  formData.append("file", file, file.name);

  const { uploadURL } = await getImageUploadUrl();

  const response = await axios.post(uploadURL, formData, {
    onUploadProgress: wrapAxiosProgress(onUploadProgress),
  });

  return response.data;
};

export const uploadVideoToCloudflare = async (
  file: File,
  onUploadProgress: UploadProgressEvent,
) => {
  const formData = new FormData();
  formData.append("file", file, file.name);

  const { uploadURL, uid } = await getVideoUploadUrl();

  await axios.post(uploadURL, formData, {
    onUploadProgress: wrapAxiosProgress(onUploadProgress),
  });

  return { result: { uid } };
};

export const uploadVideoToCloudflareWithTus = async (
  file: File,
  onUploadProgress: UploadProgressEvent,
) => {
  return new Promise<{
    uid: string | undefined;
  }>((resolve, reject) => {
    let uid: string | undefined;

    new tus.Upload(file, {
      endpoint: `${LGT_SERVER_URL}/videos/tus_upload_url`,
      // Without chunkSize, tus-js-client sends the whole file in one PATCH.
      // Cloudflare rejects / resets chunks over 200MB.
      chunkSize: CLOUDFLARE_TUS_CHUNK_SIZE,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      metadata: {
        filename: file.name,
        filetype: file.type,
        name: file.name,
      },
      onError: (event) => {
        reject(event);
      },
      onProgress: onUploadProgress,
      onSuccess: () => {
        resolve({ uid });
      },
      onBeforeRequest: (req) => {
        const url = req.getURL();
        // only set authorization for LGT Server endpoint to create upload
        // the Cloudflare endpoint from the create upload will reject
        // the Authorization header and fail the upload
        if (url.includes(LGT_SERVER_URL)) {
          req.setHeader(
            "Authorization",
            `Bearer ${localStorage.getItem("token")}`,
          );
        }
      },
      // @ts-expect-error unused variable req
      onAfterResponse: (req, res) => {
        uid = res.getHeader("stream-media-id");
      },
    }).start();
  });
};

export const uploadTrackToCloudflareR2 = async (
  file: File,
  onUploadProgress: UploadProgressEvent,
) => {
  const { url, object_key, uid } = await getTrackUploadUrl(file.name);

  // file should be the only thing in the body for R2 S3 compatible uploads
  // otherwise, the upload becomes multipart/form-data in the bucket
  // and downloads will have multipart/form-data header instead of audio/[mp3, ogg, etc]
  await axios.put(url, file, {
    headers: {
      // Cloudflare returns this back as the content type during download
      // the browser would open music/video files instead of downloading them
      "Content-Type": file.type,
    },
    onUploadProgress: wrapAxiosProgress(onUploadProgress),
  });

  return { result: { object_key, uid } };
};

export const pollForVideoProcessing = async (id: number) => {
  return new Promise<Video>((resolve, reject) => {
    const interval = setInterval(async () => {
      try {
        const video = await getVideo(id);
        if (video.ready_to_stream) {
          clearInterval(interval);
          resolve(video);
        }
      } catch (error) {
        clearInterval(interval);
        reject(error);
      }
    }, 5000);
  });
};

enum FileSizeValidationStatus {
  Success = "Success",
  WidthTooSmall = "WidthTooSmall",
  ExceedsDimensions = "ExceedsDimensions",
  ExceedsMaxSize = "ExceedsMaxSize",
  UnknownError = "UnknownError",
}

type FileValidationSuccessResult = {
  status: FileSizeValidationStatus.Success;
};

type FileValidationExceedsDimensionsResult = {
  status: FileSizeValidationStatus.ExceedsDimensions;
  actualWidth: number;
  actualHeight: number;
  maxWidth: number;
  maxHeight: number;
};

type FileValidationExceedsMaxSizeResult = {
  status: FileSizeValidationStatus.ExceedsMaxSize;
  maxFileSize: number;
};

type FileValidationUnknownErrorResult = {
  status: FileSizeValidationStatus.UnknownError;
};

type FileValidationWidthTooSmallResult = {
  status: FileSizeValidationStatus.WidthTooSmall;
  minWidth: number;
};

type FileValidationResult =
  | FileValidationSuccessResult
  | FileValidationExceedsDimensionsResult
  | FileValidationExceedsMaxSizeResult
  | FileValidationUnknownErrorResult
  | FileValidationWidthTooSmallResult;

export const validateFileSize = (
  options: UploadFileOptions,
): Promise<FileValidationResult> => {
  const { file, fileType } = options;

  const maxFileSize = options.maxFileSize ?? MaxFileSizeByFileType[fileType];

  if (file.size > maxFileSize) {
    return Promise.resolve({
      status: FileSizeValidationStatus.ExceedsMaxSize,
      maxFileSize,
    });
  }

  if (fileType === FileType.IMAGE) {
    return new Promise((resolve) => {
      const image = new Image();
      const objectURL = window.URL.createObjectURL(file);

      image.onload = () => {
        const maxWidth = options.maxWidth ?? MAX_IMAGE_WIDTH;
        const maxHeight = options.maxHeight ?? MAX_IMAGE_HEIGHT;
        const minWidth = options.minWidth ?? 0;

        if (image.naturalWidth < minWidth) {
          resolve({ status: FileSizeValidationStatus.WidthTooSmall, minWidth });
        } else if (
          image.naturalWidth > maxWidth ||
          image.naturalHeight > maxHeight
        ) {
          resolve({
            status: FileSizeValidationStatus.ExceedsDimensions,
            actualWidth: image.naturalWidth,
            actualHeight: image.naturalHeight,
            maxWidth,
            maxHeight,
          });
        } else {
          resolve({ status: FileSizeValidationStatus.Success });
        }

        window.URL.revokeObjectURL(objectURL);
      };

      image.onerror = () => {
        window.URL.revokeObjectURL(objectURL);
        resolve({ status: FileSizeValidationStatus.UnknownError });
      };

      image.src = objectURL;
    });
  }

  return Promise.resolve({ status: FileSizeValidationStatus.Success });
};

export const getFileSizeValidationErrorMessage = (
  result: FileValidationResult,
  fileName: string,
) => {
  switch (result.status) {
    case FileSizeValidationStatus.WidthTooSmall:
      return `${fileName} is too small. The minimum width is ${result.minWidth}px`;
    case FileSizeValidationStatus.ExceedsDimensions: {
      const widthExceeded = result.actualWidth > result.maxWidth;
      const heightExceeded = result.actualHeight > result.maxHeight;

      if (widthExceeded && heightExceeded) {
        return `${fileName} dimensions (${result.actualWidth}×${result.actualHeight}px) exceed limits (${result.maxWidth}×${result.maxHeight}px)`;
      } else if (widthExceeded) {
        return `${fileName} width (${result.actualWidth}px) exceeds the maximum allowed width of ${result.maxWidth}px`;
      } else {
        return `${fileName} height (${result.actualHeight}px) exceeds the maximum allowed height of ${result.maxHeight}px`;
      }
    }
    case FileSizeValidationStatus.ExceedsMaxSize:
      return `${fileName} exceeds the maximum allowed size of ${getReadableFileSize(result.maxFileSize)}`;
    case FileSizeValidationStatus.UnknownError:
      return "An unknown error occurred while validating file size";
    default:
      return undefined;
  }
};

const getReadableFileSize = (fileSize: number) => {
  if (fileSize < 1024) {
    return `${fileSize} bytes`;
  }

  if (fileSize < 1024 * 1024) {
    return formatFileSize(fileSize / 1024, "KB");
  }

  if (fileSize < 1024 * 1024 * 1024) {
    return formatFileSize(fileSize / 1024 / 1024, "MB");
  }

  return formatFileSize(fileSize / 1024 / 1024 / 1024, "GB");
};

const formatFileSize = (value: number, unit: string) => {
  const rounded = parseFloat(value.toFixed(1));
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toString()} ${unit}`;
};

export type UploadFileOptions = {
  file: File;
  fileType: FileType;
  onProgress: UploadProgressEvent;
  minWidth?: number;
  maxWidth?: number;
  maxHeight?: number;
  maxFileSize?: number;
};

export const uploadMediaFile = async (options: UploadFileOptions) => {
  const { file, fileType, maxFileSize, onProgress } = options;

  const validationResult = await validateFileSize({
    ...options,
    maxFileSize: maxFileSize ?? MaxFileSizeByFileType[fileType],
  });

  if (validationResult.status !== FileSizeValidationStatus.Success) {
    toast.error(`File upload failed`, {
      description: getFileSizeValidationErrorMessage(
        validationResult,
        file.name,
      ),
      icon: createElement(CircleAlert, {
        className: "size-5 text-red-600 mr-2",
      }),
      duration: Infinity,
    });

    return;
  }

  let upload: Promise<UploadedFile> | undefined;

  if (fileType === FileType.IMAGE) {
    upload = uploadImage(file, onProgress);
  } else if (fileType === FileType.VIDEO) {
    upload = uploadVideo(file, onProgress);
  } else if (fileType === FileType.AUDIO) {
    upload = uploadAudio(file, onProgress);
  }

  if (upload) {
    const toastMetadata = getUploadToastMetadata(fileType, file.name);

    toast.promise(upload, {
      loading: toastMetadata.loading,
      description: toastMetadata.loadingDescription,
      success: (data) => {
        trackEvent(AnalyticsEvent.FILE_UPLOADED, {
          file_type: data.fileType,
          file_name: data.fileName,
          url: data.url,
        });
        return {
          message: toastMetadata.successMessage,
          description: toastMetadata.successDescription,
        };
      },
      error: () => ({
        message: toastMetadata.failureMessage,
        description: toastMetadata.failureDescription,
      }),
    });

    const uploadedFile = await upload;

    return uploadedFile;
  }
};

const uploadImage = async (file: File, onProgress: UploadProgressEvent) => {
  const { result } = await uploadImageToCloudflare(file, onProgress);

  const image = await createImage({
    cloudflare_image_id: result.id,
    file_name: file.name,
  });

  return {
    url: image.url,
    fileName: image.file_name,
    fileType: FileType.IMAGE,
  } as const;
};

const uploadVideo = async (file: File, onProgress: UploadProgressEvent) => {
  const { uid } = await uploadVideoToCloudflareWithTus(file, onProgress);

  if (!uid) {
    throw new Error("Failed to upload video to Cloudflare");
  }

  const video = await createVideo({
    cloudflare_video_uid: uid,
    file_name: file.name,
  });

  return {
    url: video.hls_url,
    fileName: video.file_name,
    fileType: FileType.VIDEO,
  } as const;
};

const uploadAudio = async (file: File, onProgress: UploadProgressEvent) => {
  const { result } = await uploadTrackToCloudflareR2(file, onProgress);

  const track = await createTrack({
    object_key: result.object_key,
    uid: result.uid,
    file_name: file.name,
    file_size_bytes: file.size,
  });

  return {
    url: track.url,
    fileName: track.file_name,
    fileType: FileType.AUDIO,
  } as const;
};

// Custom uploaders

export type UploadUniqueContentCSVOptions = UploadFileOptions & {
  skuId: string;
};

export const uploadUniqueContentCSV = async ({
  file,
  onProgress,
  skuId,
}: UploadUniqueContentCSVOptions) => {
  if (file.size > MaxFileSizeBytes.CSV) {
    toast.error("File upload failed", {
      description: "File exceeds the maximum allowed size of 10MB",
      icon: createElement(CircleAlert, {
        className: "size-5 text-red-600 mr-2",
      }),
      duration: Infinity,
    });

    return;
  }

  const upload = updateSKUUniqueContent(skuId, {
    file,
    onUploadProgress: onProgress,
  });

  toast.promise(upload, {
    loading: "Uploading unique content...",
    description: "Do not close this browser tab or navigate away",
    success: (data) => ({
      message: "Unique content uploaded",
      description: data.unique_content?.metadata.file_name,
    }),
    error: () => ({
      message: "Unique content upload failed",
    }),
  });

  const sku = await upload;

  return {
    url: get(sku, "unique_content.metadata.file_name", "unique_content.csv"),
    fileName: get(
      sku,
      "unique_content.metadata.file_name",
      "unique_content.csv",
    ),
    fileType: FileType.CSV,
  } as const;
};

const getUploadToastMetadata = (fileType: FileType, fileName: string) => {
  if (fileType === FileType.IMAGE) {
    return {
      loading: "Uploading image...",
      loadingDescription: "Do not close this browser tab or navigate away",
      successMessage: "Image Uploaded",
      successDescription: fileName,
      failureMessage: "Image Upload Failed",
      failureDescription: fileName,
    };
  } else if (fileType === FileType.VIDEO) {
    return {
      loading: "Uploading video...",
      loadingDescription: "Do not close this browser tab or navigate away",
      successMessage: "Video Uploaded",
      successDescription: fileName,
      failureMessage: "Video Upload Failed",
      failureDescription: fileName,
    };
  } else if (fileType === FileType.AUDIO) {
    return {
      loading: "Uploading audio...",
      loadingDescription: "Do not close this browser tab or navigate away",
      successMessage: "Audio Uploaded",
      successDescription: fileName,
      failureMessage: "Audio Upload Failed",
      failureDescription: fileName,
    };
  }

  throw new Error(
    "[getUploadToastMetadata]: Unrecognized file type: " + fileType,
  );
};
